<template>
  <div class="project-progress-container">
    <!-- 标题区域 -->
    <div class="title-section">
      <h3 class="title">形象进度</h3>
    </div>

    <!-- Loading 状态 -->
    <div v-if="loading" class="loading-section">
      <div class="loading-spinner"></div>
      <p class="loading-text">加载中...</p>
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error-section">
      <div class="error-icon">⚠️</div>
      <p class="error-text">{{ error }}</p>
      <button class="retry-btn" @click="fetchProgressData">重试</button>
    </div>

    <!-- 空状态 -->
    <div v-else-if="progressList.length === 0" class="empty-section">
      <div class="empty-icon">📋</div>
      <p class="empty-text">暂无形象进度记录 {{ env.project.zzl_id }}</p>
    </div>

    <!-- 进度列表 -->
    <div v-else class="progress-list" ref="progressContainer">
      <div
        v-for="(item, index) in displayedProgressList"
        :key="index"
        class="progress-item"
      >
        <!-- 时间线点 -->
        <div class="timeline-dot"></div>

        <!-- 进度内容 -->
        <div class="progress-content">
          <!-- 时间标签 -->
          <div class="progress-time">
            {{ formatDate(item.created_at) }}
          </div>

          <!-- 描述内容 -->
          <div class="progress-description">
            {{ item.content }}
          </div>

          <!-- 文件附件展示 -->
          <div v-if="item.files && item.files.length > 0" class="progress-files-container">
            <!-- 分离图片和其他文件 -->
            <div v-if="getImageFiles(item.files).length > 0" class="images-section">
              <!-- 单张图片 -->
              <div v-if="getImageFiles(item.files).length === 1" class="single-image-container">
                <img
                  :src="getImageFiles(item.files)[0]"
                  class="progress-image"
                  @error="handleImageError"
                  @click="openImagePreview(getImageFiles(item.files)[0])"
                />
              </div>

              <!-- 多张图片网格 -->
              <div v-else class="multiple-images-container">
                <div class="images-grid" :class="`grid-${Math.min(getImageFiles(item.files).length, 4)}`">
                  <div
                    v-for="(image, imgIndex) in getImageFiles(item.files).slice(0, 4)"
                    :key="imgIndex"
                    class="image-item"
                    @click="openImageCarousel(getImageFiles(item.files), imgIndex)"
                  >
                    <img
                      :src="image"
                      class="grid-image"
                      @error="handleImageError"
                    />
                    <!-- 显示更多图片的遮罩 -->
                    <div v-if="imgIndex === 3 && getImageFiles(item.files).length > 4" class="more-images-overlay">
                      <span class="more-count">+{{ getImageFiles(item.files).length - 4 }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 其他文件附件 -->
            <div v-if="getOtherFiles(item.files).length > 0" class="attachments-section">
              <div class="attachments-title">附件</div>
              <div class="attachments-list">
                <div
                  v-for="(file, fileIndex) in getOtherFiles(item.files)"
                  :key="fileIndex"
                  class="attachment-item"
                  @click="downloadFile(file)"
                >
                  <div class="attachment-icon">
                    <span v-if="getFileType(file) === 'pdf'">📄</span>
                    <span v-else-if="getFileType(file) === 'doc'">📝</span>
                    <span v-else-if="getFileType(file) === 'xls'">📊</span>
                    <span v-else-if="getFileType(file) === 'zip'">🗜️</span>
                    <span v-else>📎</span>
                  </div>
                  <div class="attachment-info">
                    <div class="attachment-name">{{ getFileName(file) }}</div>
                    <div class="attachment-type">{{ getFileExtension(file).toUpperCase() }} 文件</div>
                  </div>
                  <div class="attachment-action">
                    <span class="download-icon">⬇️</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 更多按钮 -->
      <div v-if="hasMoreItems" class="more-button-container">
        <button class="more-btn" @click="goToDetailPage">
          <span class="more-text">查看更多</span>
          <span class="more-count-badge">+{{ remainingItemsCount }}</span>
          <span class="more-arrow">→</span>
        </button>
      </div>
    </div>

    <!-- 图片预览对话框 -->
    <div v-if="showImagePreview" class="image-preview-overlay" @click="closeImagePreview">
      <div class="image-preview-content" @click.stop>
        <div class="image-preview-header">
          <h4>图片预览</h4>
          <button class="close-btn" @click="closeImagePreview">×</button>
        </div>
        <div class="image-preview-body">
          <img :src="previewImageUrl" class="preview-image" />
        </div>
      </div>
    </div>

    <!-- 图片轮播对话框 -->
    <div v-if="showImageCarousel" class="image-preview-overlay" @click="closeImageCarousel">
      <div class="image-carousel-content" @click.stop>
        <div class="image-preview-header">
          <h4>图片预览 ({{ currentCarouselIndex + 1 }}/{{ carouselImages.length }})</h4>
          <button class="close-btn" @click="closeImageCarousel">×</button>
        </div>
        <div class="carousel-preview-body">
          <div class="carousel-image-wrapper">
            <img :src="carouselImages[currentCarouselIndex]" class="carousel-preview-image" @error="handleImageError" />

            <!-- 轮播控制按钮 -->
            <div v-if="carouselImages.length > 1" class="carousel-nav-controls">
              <button
                class="nav-btn prev-nav-btn"
                @click="prevCarouselImage"
                :disabled="currentCarouselIndex === 0"
              >
                <span>‹</span>
              </button>
              <button
                class="nav-btn next-nav-btn"
                @click="nextCarouselImage"
                :disabled="currentCarouselIndex === carouselImages.length - 1"
              >
                <span>›</span>
              </button>
            </div>
          </div>

          <!-- 缩略图导航 -->
          <div v-if="carouselImages.length > 1" class="thumbnail-nav">
            <div
              v-for="(image, index) in carouselImages"
              :key="index"
              class="thumbnail-item"
              :class="{ active: currentCarouselIndex === index }"
              @click="currentCarouselIndex = index"
            >
              <img :src="image" class="thumbnail-image" @error="handleImageError" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    env: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      error: null,
      progressList: [],
      showImagePreview: false,
      previewImageUrl: '',
      showImageCarousel: false,
      carouselImages: [],
      currentCarouselIndex: 0,
      maxDisplayItems: 3, // 最大显示条数
      defaultImage: 'https://nbgzfiles-1257839135.cos.ap-shanghai.myqcloud.com/assets/rwxavatar.png',
    };
  },
  computed: {
    displayedProgressList() {
      return this.progressList.slice(0, this.maxDisplayItems);
    },

    hasMoreItems() {
      return this.progressList.length > this.maxDisplayItems;
    },

    remainingItemsCount() {
      return Math.max(0, this.progressList.length - this.maxDisplayItems);
    },
  },
  mounted() {
    this.fetchProgressData();
    this.calculateMaxItems();
    // 监听窗口大小变化，重新计算显示条数
    window.addEventListener('resize', this.calculateMaxItems);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.calculateMaxItems);
  },
  watch: {
    'env.project.id': {
      handler() {
        if (this.env?.project?.id) {
          this.fetchProgressData();
        }
      },
    },
  },
  methods: {
    async fetchProgressData() {
      if (!this.env?.project?.id) {
        this.error = '项目ID不存在';
        return;
      }

      const pid = this.env.project.zzl_id;
      this.loading = true;
      this.error = null;

      try {
        const response = await this.$api.get(`/logined_zzl/project_face_progress?pid=${pid}`);

        if (response.data && response.data.data) {
          this.progressList = Array.isArray(response.data.data) ?
            response.data.data :
            [response.data.data];

          // 按时间倒序排列
          this.progressList.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        } else {
          this.progressList = [];
        }
      } catch (error) {
        console.error('获取形象进度失败:', error);
        this.error = '获取形象进度失败，请稍后重试';
        this.progressList = [];
      } finally {
        this.loading = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return '未知时间';

      try {
        const date = new Date(dateString);
        const now = new Date();
        const diff = now - date;
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));

        if (days === 0) {
          return '今天';
        } else if (days === 1) {
          return '昨天';
        } else if (days < 7) {
          return `${days}天前`;
        } else {
          return date.toLocaleDateString('zh-CN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          });
        }
      } catch (error) {
        return dateString;
      }
    },

    handleImageError(event) {
      // 图片加载失败时使用默认图片
      event.target.src = this.defaultImage;
    },

    openImagePreview(imageUrl) {
      this.previewImageUrl = imageUrl;
      this.showImagePreview = true;
    },

    closeImagePreview() {
      this.showImagePreview = false;
      this.previewImageUrl = '';
    },

    openImageCarousel(images, index) {
      this.carouselImages = images;
      this.currentCarouselIndex = index;
      this.showImageCarousel = true;
    },

    closeImageCarousel() {
      this.showImageCarousel = false;
      this.carouselImages = [];
      this.currentCarouselIndex = 0;
    },

    prevCarouselImage() {
      if (this.currentCarouselIndex > 0) {
        this.currentCarouselIndex--;
      }
    },

    nextCarouselImage() {
      if (this.currentCarouselIndex < this.carouselImages.length - 1) {
        this.currentCarouselIndex++;
      }
    },

    getImageFiles(files) {
      return files.filter((file) => this.isImageFile(file));
    },

    getOtherFiles(files) {
      return files.filter((file) => !this.isImageFile(file));
    },

    isImageFile(fileUrl) {
      const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];
      const extension = this.getFileExtension(fileUrl).toLowerCase();
      return imageExtensions.includes(extension);
    },

    getFileType(fileUrl) {
      const extension = this.getFileExtension(fileUrl).toLowerCase();
      if (['pdf'].includes(extension)) return 'pdf';
      if (['doc', 'docx'].includes(extension)) return 'doc';
      if (['xls', 'xlsx'].includes(extension)) return 'xls';
      if (['zip', 'rar', '7z'].includes(extension)) return 'zip';
      if (['txt'].includes(extension)) return 'txt';
      return 'file';
    },

    getFileExtension(fileUrl) {
      const filename = fileUrl.split('/').pop().split('?')[0]; // 移除查询参数
      return filename.includes('.') ? filename.split('.').pop() : '';
    },

    getFileName(fileUrl) {
      const filename = fileUrl.split('/').pop().split('?')[0]; // 移除查询参数
      return filename.includes('.') ? filename.split('.').slice(0, -1).join('.') : filename;
    },

    downloadFile(fileUrl) {
      // 创建一个隐藏的下载链接
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = this.getFileName(fileUrl) + '.' + this.getFileExtension(fileUrl);
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    goToDetailPage() {
      if (this.env?.project?.id) {
        this.$router.push(`/core/project/${this.env.project.id}/faceproc`);
      }
    },

    calculateMaxItems() {
      this.$nextTick(() => {
        const container = this.$refs.progressContainer;
        if (container) {
          const containerHeight = container.clientHeight;
          const itemHeight = 120; // 估算每个进度项的平均高度
          const buttonHeight = 60; // 更多按钮区域高度
          const availableHeight = containerHeight - buttonHeight;
          const maxItems = Math.floor(availableHeight / itemHeight);
          this.maxDisplayItems = Math.max(1, Math.min(maxItems, 5)); // 最少1条，最多5条
        }
      });
    },
  },
};
</script>

<style scoped>
.project-progress-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px;
  box-sizing: border-box;
}

.title-section {
  margin-bottom: 16px;
}

.title {
  font-size: 18px;
  font-weight: bold;
  margin: 0;
  color: var(--text-primary, #333);
}

/* Loading 状态 */
.loading-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--border, #f0f0f0);
  border-top: 3px solid var(--primary, #007bff);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  color: var(--text-secondary, #666);
  font-size: 14px;
  margin: 0;
}

/* 错误状态 */
.error-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.error-icon {
  font-size: 32px;
}

.error-text {
  color: var(--text-secondary, #666);
  font-size: 14px;
  margin: 0;
  text-align: center;
}

.retry-btn {
  background: var(--primary, #007bff);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.retry-btn:hover {
  background: var(--primary-dark, #0056b3);
}

/* 空状态 */
.empty-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.empty-icon {
  font-size: 32px;
  opacity: 0.6;
}

.empty-text {
  color: var(--text-secondary, #666);
  font-size: 14px;
  margin: 0;
}

/* 进度列表 */
.progress-list {
  flex: 1;
  position: relative;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.progress-list::before {
  content: '';
  position: absolute;
  left: 9px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: var(--border, #e0e0e0);
}

.progress-item {
  position: relative;
  margin-bottom: 24px;
  display: flex;
  align-items: flex-start;
}

.progress-item:last-child {
  margin-bottom: 0;
}

.timeline-dot {
  position: absolute;
  left: -15px;
  top: 8px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--primary, #007bff);
  border: 2px solid white;
  box-shadow: 0 0 0 2px var(--border, #e0e0e0);
  z-index: 1;
}

.progress-content {
  flex: 1;
  margin-left: 12px;
}

.progress-time {
  font-size: 12px;
  color: var(--text-secondary, #999);
  margin-bottom: 8px;
  font-weight: 500;
}

.progress-description {
  color: var(--text-primary, #333);
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 12px;
  word-wrap: break-word;
}

.progress-files-container {
  margin-top: 8px;
}

.images-section {
  margin-bottom: 8px;
}

.attachments-section {
  margin-top: 12px;
}

.attachments-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary, #666);
  margin-bottom: 8px;
}

.attachments-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.attachment-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border: 1px solid var(--border, #e0e0e0);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: var(--bg-secondary, #fafafa);
}

.attachment-item:hover {
  border-color: var(--primary, #007bff);
  background: var(--bg-hover, #f0f8ff);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.1);
}

.attachment-icon {
  font-size: 18px;
  margin-right: 10px;
  flex-shrink: 0;
}

.attachment-info {
  flex: 1;
  min-width: 0;
}

.attachment-name {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary, #333);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 2px;
}

.attachment-type {
  font-size: 11px;
  color: var(--text-secondary, #999);
}

.attachment-action {
  margin-left: 8px;
  flex-shrink: 0;
}

.download-icon {
  font-size: 14px;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.attachment-item:hover .download-icon {
  opacity: 1;
}

.single-image-container {
  margin-bottom: 8px;
}

.progress-image {
  max-width: 100%;
  height: auto;
  max-height: 200px;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s;
  object-fit: cover;
}

.progress-image:hover {
  transform: scale(1.02);
}

.multiple-images-container {
  margin-top: 8px;
}

.images-grid {
  display: grid;
  gap: 4px;
  max-width: 100%;
}

.images-grid.grid-2 {
  grid-template-columns: 1fr 1fr;
}

.images-grid.grid-3 {
  grid-template-columns: 1fr 1fr 1fr;
}

.images-grid.grid-4 {
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
}

.image-item {
  position: relative;
  aspect-ratio: 1;
  overflow: hidden;
  border-radius: 6px;
}

.grid-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.2s;
}

.grid-image:hover {
  transform: scale(1.05);
}

.more-images-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.more-count {
  color: white;
  font-size: 14px;
  font-weight: bold;
}

/* 图片预览对话框 */
.image-preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.image-preview-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.image-preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid var(--border, #eee);
}

.image-preview-header h4 {
  margin: 0;
  font-size: 18px;
  color: var(--text-primary, #333);
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--text-secondary, #666);
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}

.close-btn:hover {
  background: var(--border, #f0f0f0);
}

.image-preview-body {
  padding: 20px;
  text-align: center;
  overflow: auto;
}

.preview-image {
  max-width: 100%;
  max-height: 60vh;
  object-fit: contain;
  border-radius: 8px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .project-progress-container {
    padding: 12px;
  }

  .progress-list {
    padding-left: 16px;
  }

  .timeline-dot {
    left: -11px;
  }

  .progress-content {
    margin-left: 8px;
  }

  .images-grid.grid-3 {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr;
  }

  .images-grid.grid-4 {
    grid-template-columns: 1fr 1fr;
  }

  .attachment-item {
    padding: 6px 10px;
  }

  .attachment-name {
    font-size: 12px;
  }

  .attachment-type {
    font-size: 10px;
  }

  .attachment-icon {
    font-size: 16px;
    margin-right: 8px;
  }
}

/* 图片轮播对话框样式 */
.image-carousel-content {
  background: white;
  border-radius: 12px;
  width: 95%;
  max-width: 900px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.carousel-preview-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: hidden;
}

.carousel-image-wrapper {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.carousel-preview-image {
  max-width: 100%;
  max-height: 60vh;
  object-fit: contain;
  border-radius: 8px;
}

.carousel-nav-controls {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding: 0 16px;
  pointer-events: none;
}

.nav-btn {
  background: rgba(0, 0, 0, 0.6);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  color: white;
  font-size: 20px;
  cursor: pointer;
  pointer-events: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.nav-btn:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.8);
}

.nav-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.thumbnail-nav {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 8px 0;
  justify-content: center;
}

.thumbnail-item {
  flex-shrink: 0;
  width: 60px;
  height: 60px;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  transition: border-color 0.2s;
}

.thumbnail-item.active {
  border-color: var(--primary, #007bff);
}

.thumbnail-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 更多按钮样式 */
.more-button-container {
  display: flex;
  justify-content: center;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px dashed var(--border, #e0e0e0);
}

.more-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, var(--primary, #007bff), var(--primary-dark, #0056b3));
  color: white;
  border: none;
  border-radius: 20px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.2);
}

.more-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

.more-text {
  font-weight: 500;
}

.more-count-badge {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 2px 6px;
  font-size: 11px;
  font-weight: bold;
}

.more-arrow {
  font-size: 14px;
  transition: transform 0.2s ease;
}

.more-btn:hover .more-arrow {
  transform: translateX(2px);
}
</style>
